import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppComponent } from './app.component';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatExpansionModule } from '@angular/material/expansion'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { MatPaginatorIntlJa } from './shared/mat-paginator-intl-ja';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { FormsModule } from '@angular/forms';
import { NavigationComponent } from './pages/navigation/navigation.component';
import { LayoutModule } from '@angular/cdk/layout';
import { OwnHistoriesComponent } from './pages/own-histories/own-histories.component';


import { AppRoutingModule } from './app-routing.module';

import { ReactiveFormsModule } from '@angular/forms';
import { provideFirebaseApp, initializeApp, FirebaseApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore } from '@angular/fire/firestore';
import { provideAnalytics, getAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { initializeFirestore } from 'firebase/firestore';
import { environment } from './../environments/environment';
import { AuthService } from './services/auth.service';
import { AuthGuard } from './guard/auth.guard';
import { UserLoginComponent } from './pages/user-login/user-login.component';
import { UserSignUpComponent } from './pages/user-signup/user-signup.component';
import { UserInfoComponent } from './pages/user-info/user-info.component';

import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';

import { NgxIndexedDBModule, DBConfig } from 'ngx-indexed-db';

import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { RequestDialogComponent } from './pages/request-dialog/request-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { ApproverGuard } from './guard/approver.guard';
import { RequestApproveComponent } from './pages/request-approve/request-approve.component';
import { PayrollReportComponent } from './pages/payroll-report/payroll-report.component';
import { ReportService } from './services/report.service';
import { TeamManageComponent } from './pages/team-manage/team-manage.component';
import { MaintenerGuard } from './guard/maintener.guard';
import { TeamService } from './services/team.service';
import { UserManageComponent } from './pages/user-manage/user-manage.component';
import { UserService } from './services/user.service';
import { TerminalManageComponent } from './pages/terminal-manage/terminal-manage.component';
import { TerminalService } from './services/terminal.service';
import { TerminalManageDialogComponent } from './pages/terminal-manage-dialog/terminal-manage-dialog.component';
import { UserManageDialogComponent } from './pages/user-manage-dialog/user-manage-dialog.component';
import { StackLogService } from './services/stack-log.service';

import { HttpClientModule, HttpClient } from '@angular/common/http';
import { TeamManageDialogComponent } from './pages/team-manage-dialog/team-manage-dialog.component';
import { PasswordResetComponent } from './pages/password-reset/password-reset.component';
import { AdminGuard } from './guard/admin.guard';
import { ConfirmDialogComponent } from './pages/confirm-dialog/confirm-dialog.component';
import { ImportUserDialogComponent } from './pages/import-user-dialog/import-user-dialog.component';
import { DatePipe } from '@angular/common';
import { DateDetailDialogComponent } from './pages/date-detail-dialog/date-detail-dialog.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RouterModule } from '@angular/router';
import { PasswordChangeComponent } from './pages/password-change/password-change.component';
import { PersonalSettingsComponent } from './pages/personal-settings/personal-settings.component'
import { OwnHistoriesDetailComponent } from './pages/own-histories-detail/own-histories-detail.component';
import { SelectTimeDialogComponent } from './pages/select-time-dialog/select-time-dialog.component';
import { ShiftStampDialogComponent } from './pages/shift-stamp-dialog/shift-stamp-dialog.component';
import { RequestApproveDetailComponent } from './pages/request-approve-detail/request-approve-detail.component';
import { ActualStampTableComponent } from './pages/actual-stamp-table/actual-stamp-table.component';
import { ChangeStampTableComponent } from './pages/change-stamp-table/change-stamp-table.component';
import { PunchStampComponent } from './pages/punch-stamp/punch-stamp.component';
import { TerminalLoginComponent } from './pages/terminal-login/terminal-login.component';
import { TerminalNavigationComponent } from './pages/terminal-navigation/terminal-navigation.component';
import { AudioService } from './services/audio.service';
import { ObcApiService } from './services/obc-api.service';
import { inject, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeJa from '@angular/common/locales/ja';
// import { FileHelpersModule } from 'ngx-file-helpers';

registerLocaleData(localeJa);

const dbConfig: DBConfig = {
  name: 'workTimeDB',
  version: 3,
  objectStoresMeta: [
    {
      store: 'time-stamp',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'uid', keypath: 'uid', options: { unique: false } },
        { name: 'doc', keypath: 'doc', options: { unique: false } },
      ]
    },
    {
      store: 'log-store',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'timeStamp', keypath: 'timeStamp', options: { unique: false } },
        { name: 'serialId', keypath: 'serialId', options: { unique: false } },
      ]
    }
  ],
};

@NgModule({
    declarations: [
        AppComponent,
        NavigationComponent,
        OwnHistoriesComponent,
        UserLoginComponent,
        UserSignUpComponent,
        UserInfoComponent,
        RequestDialogComponent,
        RequestApproveComponent,
        PayrollReportComponent,
        TeamManageComponent,
        UserManageComponent,
        TerminalManageComponent,
        TerminalManageDialogComponent,
        UserManageDialogComponent,
        TeamManageDialogComponent,
        PasswordResetComponent,
        PasswordChangeComponent,
        ConfirmDialogComponent,
        ImportUserDialogComponent,
        DateDetailDialogComponent,
        PersonalSettingsComponent,
        OwnHistoriesDetailComponent,
        SelectTimeDialogComponent,
        ShiftStampDialogComponent,
        RequestApproveDetailComponent,
        ActualStampTableComponent,
        ChangeStampTableComponent,
        PunchStampComponent,
        TerminalLoginComponent,
        TerminalNavigationComponent,
    ],
    imports: [
        BrowserModule.withServerTransition({ appId: 'serverApp' }),
        BrowserAnimationsModule,
        MatButtonModule,
        MatCheckboxModule,
        FormsModule,
        LayoutModule,
        MatToolbarModule,
        MatSidenavModule,
        MatIconModule,
        MatListModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSelectModule,
        MatExpansionModule,
        MatSnackBarModule,
        MatChipsModule,
        MatDividerModule,
        MatTabsModule,
        MatAutocompleteModule,
        MatTooltipModule,
        MatRadioModule,
        AppRoutingModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        MatMenuModule,
        HttpClientModule,
        MatBadgeModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:30000'
        }),
        RouterModule,
        NgxIndexedDBModule.forRoot(dbConfig)
        // AngularIndexedDB,
        // FileHelpersModule,
    ],
    providers: [
        AudioService,
        AuthService,
        ReportService,
        TeamService,
        UserService,
        TerminalService,
        AuthGuard,
        ApproverGuard,
        MaintenerGuard,
        HttpClient,
        AdminGuard,
        DatePipe,
        ScreenTrackingService,
        UserTrackingService,
        StackLogService,
        ObcApiService,
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),
        provideFirestore(() => {
            const app = inject(FirebaseApp);
            return initializeFirestore(app, { ignoreUndefinedProperties: true });
        }),
        provideAnalytics(() => {
            const platformId = inject(PLATFORM_ID);
            if (isPlatformBrowser(platformId)) {
                return getAnalytics();
            }
            return undefined;
        }),
        { provide: LOCALE_ID, useValue: 'ja-JP' },
        { provide: MAT_DATE_LOCALE, useValue: 'ja-JP' },
        { provide: MatPaginatorIntl,
            useClass: MatPaginatorIntlJa
        },
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
